import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import {
  updateAxisOptions,
  updateSeriesInAxisOptions,
} from '../../../../redux/ChartPoperties/ChartControlsActions';
import SeriesAxisOptions from './SeriesAxisOptions';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useState } from 'react';
import { palette } from '../../../..';
import DeleteIcon from '@mui/icons-material/Delete';

const measureAxisButtonOptions = [
  { type: 'Left', value: 'left' },
  { type: 'Right', value: 'right' },
];

const dimensionAxisButtonOptions = [
  { type: 'Bottom', value: 'bottom' },
  { type: 'Top', value: 'top' },
];

const positions = [
  { name: 'Start', value: 'start' },
  { name: 'Middle', value: 'middle' },
  { name: 'End', value: 'end' },
];

interface Props {
  propKey: string;
  iconColor?: string;
  backgroundColor?: string;
}

/**
 * SeriesAxisComponent is a children component and AxisOptions.tsx is parent component for this component.
 * @returns multiple series section which includes functionality to add a series if allowed and renders various options including axis position, manual min/max, axis name, tick styling and a section which contains measures having a dropdown which shows present series.
 */
const MultipleSeries = ({ propKey, iconColor, backgroundColor }: Props) => {
  const [isSeriesExpanded, setIsSeriesExpanded] = useState<{ [key: string]: boolean }>({
    '1': false,
    '2': false,
    '3': false,
    '4': false,
  });

  const { chartAxes, chartType, xAxisOptions, yAxisOptions } = useSelector((state: RootState) => ({
    chartAxes: state.chartProperties.properties[propKey]?.chartAxes || [],
    chartType: state.chartProperties.properties[propKey]?.chartType,
    xAxisOptions: state.chartControls.properties[propKey]?.axisOptions?.xAxis,
    yAxisOptions: state.chartControls.properties[propKey]?.axisOptions?.yAxis,
  }));

  const addButtonStyle = {
    backgroundColor: palette.secondary.light,
    height: '25px',
    width: '100%',
    color: 'white',
    textTransform: 'none',
    marginLeft: '4px',
  };

  const dispatch = useDispatch();
  const measureAxis = chartAxes.find((axis: any) => axis.name === 'Measure');
  const measures = measureAxis?.fields || [];

  let currentSeriesCount = 1;
  if (['horizontalBar'].includes(chartType)) {
    currentSeriesCount = Object.keys({ ...xAxisOptions }).length;
  } else {
    currentSeriesCount = Object.keys({ ...yAxisOptions }).length;
  }
  const maxSeriesAllowed = Math.min(measures.length, 4);
  const canAddMoreSeries = currentSeriesCount < maxSeriesAllowed;
  const nextSeriesNumber = currentSeriesCount + 1;

  const renderAxisButtonOptions = (series: string, axisTypeName: string) => {
    let buttonOptionName =
      axisTypeName === 'xAxis' ? dimensionAxisButtonOptions : measureAxisButtonOptions;
    return buttonOptionName.map((item) => {
      const isSelected =
        axisTypeName === 'xAxis'
          ? item.value === xAxisOptions[series].position
          : item.value === yAxisOptions[series].position;

      const handleClick = () => {
        dispatch(updateAxisOptions(propKey, axisTypeName, 'position', item.value, series));
      };

      return (
        <div
          key={item.value}
          className={isSelected ? 'radioButtonSelected' : 'radioButton'}
          onClick={handleClick}
          style={{
            backgroundColor: isSelected ? 'rgba(224, 224, 224, 1)' : 'white',
            cursor: isSelected ? 'auto' : 'pointer',
            fontWeight: isSelected ? '600' : 'normal',
            marginLeft: '0',
          }}
        >
          {item.type}
        </div>
      );
    });
  };

  const handleDeleteSeries = (seriesNumber: string) => {
    const axisOptionsToCheck = ['horizontalBar'].includes(chartType) ? xAxisOptions : yAxisOptions;
    const measuresToMove = axisOptionsToCheck[seriesNumber]?.measuresList || [];

    // Move measures from deleted series to Series 1
    if (measuresToMove.length > 0) {
      const series1MeasuresList = axisOptionsToCheck['1']?.measuresList || [];
      const updatedList = [...series1MeasuresList, ...measuresToMove];

      ['xAxis', 'yAxis'].forEach((axis) => {
        dispatch(updateAxisOptions(propKey, axis, 'measuresList', updatedList, '1'));
      });
    }

    ['xAxis', 'yAxis'].forEach((axis) => {
      const axisType = axis === 'xAxis' ? 'x' : 'y';
      dispatch(updateSeriesInAxisOptions(propKey, seriesNumber, 'remove', axisType));
    });
  };

  return (
    <>
      {/* all series section */}
      {(['horizontalBar'].includes(chartType)
        ? Object.keys(xAxisOptions)
        : Object.keys(yAxisOptions)
      ).map((key: string) => {
        return (
          <div key={key}>
            <Box
              style={{
                display: 'flex',
                marginBottom: '10px',
                justifyContent: 'space-between',
                paddingLeft: '0.5rem',
              }}
            >
              <span
                style={{
                  fontWeight: 800,
                  fontSize: '12px',
                  color: iconColor ? iconColor : '#515050',
                  textTransform: 'uppercase',
                }}
              >
                Series {key}
              </span>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
                {key !== '1' && (
                  <span style={{ marginTop: '-4px' }}>
                    <Tooltip
                      title="Delete Series"
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <DeleteIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSeries(key);
                        }}
                        sx={{
                          cursor: 'pointer',
                          color: 'rgba(0, 0, 0, 0.54)',
                          height: '14px',
                          width: '14px',
                        }}
                      />
                    </Tooltip>
                  </span>
                )}
                <Tooltip
                  title={isSeriesExpanded[key] ? 'Click to Collapse' : 'Click to Expand'}
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    disableRipple
                    disableFocusRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      let expandCollapseValue = isSeriesExpanded[key];
                      setIsSeriesExpanded((prev) => ({
                        ...prev,
                        [key]: !expandCollapseValue,
                      }));
                    }}
                    aria-label={isSeriesExpanded[key] ? 'Collapse Series' : 'Expand Series'}
                    sx={{
                      cursor: 'default',
                      paddingTop: '3px',
                      transform: 'TranslateY(-5px)',
                      color: iconColor ? iconColor : '#404040',

                      '&:focus, &:active': {
                        outline: 'none',
                      },
                    }}
                  >
                    {isSeriesExpanded[key] ? (
                      <KeyboardArrowDownIcon fontSize="medium" />
                    ) : (
                      <ChevronRightIcon fontSize="medium" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {isSeriesExpanded[key] && (
              <>
                <div
                  className="radioButtons"
                  style={{
                    marginTop: '5px',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {['horizontalBar'].includes(chartType)
                    ? renderAxisButtonOptions(key, 'xAxis')
                    : renderAxisButtonOptions(key, 'yAxis')}
                </div>
                {/* Series-specific Axis Name and Tick Options */}

                <SeriesAxisOptions
                  propKey={propKey}
                  seriesNumber={key}
                  axisType={['horizontalBar'].includes(chartType) ? 'x' : 'y'}
                  axisProps={
                    ['horizontalBar'].includes(chartType) ? xAxisOptions[key] : yAxisOptions[key]
                  }
                  positions={positions}
                  chartType={chartType}
                  iconColor={iconColor}
                  backgroundColor={backgroundColor}
                />
              </>
            )}
          </div>
        );
      })}
      {/* add series button */}
      <Box sx={{ paddingLeft: '4.5px', paddingRight: '8px' }}>
        <Tooltip
          title={
            !canAddMoreSeries
              ? currentSeriesCount >= 4
                ? "You can't add more than 4 series"
                : 'Not enough measure to add another series'
              : 'Add new series'
          }
          placement="bottom"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <span style={{ display: 'block' }}>
            <Button
              sx={{
                ...addButtonStyle,
                '&.Mui-disabled': {
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                  color: iconColor ? iconColor : '#404040',
                  opacity: 1,
                },
              }}
              disabled={!canAddMoreSeries}
              onClick={() => {
                const seriesNumberToAdd = nextSeriesNumber;
                let newPosition: string;
                ['xAxis', 'yAxis'].forEach((axis) => {
                  let axisType = axis === 'xAxis' ? 'x' : 'y';
                  if (axis === 'xAxis') {
                    const previousSeriesButtonPosition = xAxisOptions[currentSeriesCount].position;
                    newPosition = 'bottom';
                    if (previousSeriesButtonPosition === 'bottom') {
                      newPosition = 'top';
                    }
                  } else {
                    const previousSeriesButtonPosition = yAxisOptions[currentSeriesCount].position;
                    newPosition = 'left';
                    if (previousSeriesButtonPosition === 'left') {
                      newPosition = 'right';
                    }
                  }
                  dispatch(
                    updateSeriesInAxisOptions(propKey, `${seriesNumberToAdd}`, 'add', axisType),
                  );
                  const splitLineType =
                    seriesNumberToAdd === 2
                      ? 'dashed'
                      : seriesNumberToAdd === 3 || seriesNumberToAdd === 4
                        ? 'dotted'
                        : 'solid';
                  // While adding a series, `position` and `splitLineType` cannot be applied together. So we update them one after the other by looping over these options and dispatching the update separately for each.
                  ['position', 'splitLineType'].forEach((option) => {
                    const value = option === 'splitLineType' ? splitLineType : newPosition;
                    dispatch(
                      updateAxisOptions(propKey, axis, option, value, seriesNumberToAdd.toString()),
                    );
                  });
                });
              }}
            >
              Add Series
            </Button>
          </span>
        </Tooltip>
      </Box>
      {/* Measure section */}
      <div
        style={{
          marginBottom: ['horizontalBar'].includes(chartType) ? '0.8rem' : 'auto',
          marginTop: '1.2rem',
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: '12px',
            color: iconColor ? iconColor : '#515050',
            marginBottom: '0.8rem',
            textAlign: 'left',
            marginLeft: '0.5rem',
            textTransform: 'uppercase',
          }}
        >
          Measure Mapping
        </div>
        {measures.map((measure: any) => {
          return (
            <div key={measure.uId}>
              <Typography
                sx={{
                  marginBottom: '1px',
                  fontSize: '13px',
                  display: 'flex',
                  gap: '0.4rem',
                  alignItems: 'center',
                  marginInline: '0.5rem',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                {measure.displayname}
              </Typography>

              <FormControl
                fullWidth
                size="small"
                style={{
                  fontSize: '12px',
                  borderRadius: '4px',
                  paddingLeft: '7px',
                  textAlign: 'left',
                  paddingRight: '2px',
                }}
              >
                <Select
                  label=""
                  value={
                    ['horizontalBar'].includes(chartType)
                      ? Object.keys(xAxisOptions).find((key: any) =>
                          xAxisOptions[key].measuresList?.includes(measure.uId),
                        ) || '1'
                      : Object.keys(yAxisOptions).find((key: any) =>
                          yAxisOptions[key].measuresList?.includes(measure.uId),
                        ) || '1'
                  }
                  variant="outlined"
                  onChange={(event) => {
                    const newSeriesNumber = event.target.value as string;
                    let axisTypeName;
                    let axisOptionName;
                    if (['horizontalBar'].includes(chartType)) {
                      axisTypeName = 'xAxis';
                      axisOptionName = xAxisOptions;
                    } else {
                      axisTypeName = 'yAxis';
                      axisOptionName = yAxisOptions;
                    }
                    // Remove measure from other series first(if exists)
                    Object.keys(axisOptionName).forEach((key: string) => {
                      const measuresList = axisOptionName[key].measuresList || [];
                      if (measuresList.includes(measure.uId)) {
                        const updatedList = measuresList.filter((id: string) => id !== measure.uId);
                        ['xAxis', 'yAxis'].forEach((axis) => {
                          dispatch(
                            updateAxisOptions(propKey, axis, 'measuresList', updatedList, key),
                          );
                        });
                      }
                    });

                    // Add measure to the new selected series
                    const currentList = axisOptionName[newSeriesNumber].measuresList || [];
                    if (!currentList.includes(measure.uId)) {
                      const updatedList = [...currentList, measure.uId];
                      ['xAxis', 'yAxis'].forEach((axis) => {
                        dispatch(
                          updateAxisOptions(
                            propKey,
                            axis,
                            'measuresList',
                            updatedList,
                            newSeriesNumber,
                          ),
                        );
                      });
                    }
                  }}
                  sx={{
                    fontSize: '12px',
                    width: '99%',
                    margin: '0 auto 0.5rem auto',
                    // backgroundColor: 'white',
                    height: '1.5rem',
                    color: iconColor ? iconColor : '#404040',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2bb9bb',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'lightgray',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'lightgray',
                    },
                    '& .MuiSelect-icon': {
                      color: iconColor ? iconColor : '#404040',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root.Mui-selected': {
                          backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                        },
                        '& .MuiMenuItem-root.Mui-selected:hover': {
                          backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                        },
                      },
                    },
                  }}
                >
                  {(['horizontalBar'].includes(chartType)
                    ? Object.keys(xAxisOptions)
                    : Object.keys(yAxisOptions)
                  ).map((seriesNumber) => (
                    <MenuItem
                      value={seriesNumber}
                      key={seriesNumber}
                      sx={{
                        padding: '2px 10px',
                        fontSize: '12px',
                      }}
                    >
                      Series {seriesNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default MultipleSeries;
